import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { MapPin, Ruler, Bed, House } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const PROPERTY_TYPE_LABELS = {
    apartment: 'Wohnung', house: 'Haus', land: 'Grundstück',
    office: 'Büro', retail: 'Einzelhandel', warehouse: 'Lager/Halle',
    commercial_property: 'Renditeobjekt', foreign_property: 'Auslandsimmobilie', other: 'Sonstige'
};

function formatPrice(price, currency = 'EUR', marketingType = 'sale') {
    if (!price) return marketingType === 'rent' ? 'Miete auf Anfrage' : 'Kaufpreis auf Anfrage';
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(price);
}

export default function EmbedSinglePropertyPage() {
    const { id } = useParams();
    const [property, setProperty] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        axios.get(`${API}/api/public/properties/${id}`)
            .then(({ data }) => setProperty(data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [id]);

    // Auto-resize: send height to parent iframe
    useEffect(() => {
        const sendHeight = () => {
            const h = document.body.scrollHeight;
            window.parent.postMessage({ type: 'immo-embed-resize', height: h }, '*');
        };
        sendHeight();
        const observer = new ResizeObserver(sendHeight);
        observer.observe(document.body);
        return () => observer.disconnect();
    });

    if (loading) return <div className="flex items-center justify-center p-8"><div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" /></div>;
    if (!property) return <div className="p-4 text-center text-sm text-muted-foreground">Immobilie nicht gefunden</div>;

    const exposeUrl = `${API}/expose/${property.id}`;

    return (
        <div className="p-2" data-testid="embed-single-property">
            <a href={exposeUrl} target="_blank" rel="noopener noreferrer" className="block no-underline">
                <Card className="border overflow-hidden hover:shadow-lg transition-shadow cursor-pointer w-full">
                    <div className="aspect-video bg-muted relative">
                        {property.images && property.images.length > 0 ? (
                            <img
                                src={`${API}/api/public/files/${property.images[0]}`}
                                alt={property.title}
                                className="w-full h-full object-cover"
                                onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1559329146-807aff9ff1fb?w=400'; }}
                            />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center">
                                <House size={48} className="text-muted-foreground opacity-50" />
                            </div>
                        )}
                        <Badge className="absolute top-2 left-2 rounded-sm">
                            {property.marketing_type === 'rent' ? 'Miete' : 'Kauf'}
                        </Badge>
                        {(property.object_nr_external || property.external_id) && (
                            <Badge variant="secondary" className="absolute top-2 right-2 rounded-sm font-mono text-xs">
                                Obj. {property.object_nr_external || property.external_id}
                            </Badge>
                        )}
                    </div>
                    <CardContent className="p-4">
                        <h3 className="font-semibold truncate mb-1" style={{ fontFamily: 'var(--font-cabinet)' }}>
                            {property.title}
                        </h3>
                        <p className="text-sm text-muted-foreground flex items-center gap-1 mb-3">
                            <MapPin size={14} />
                            {property.address?.city || 'Keine Adresse'}
                        </p>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                            {property.area && (
                                <span className="flex items-center gap-1"><Ruler size={14} />{property.area} m²</span>
                            )}
                            {property.rooms && (
                                <span className="flex items-center gap-1"><Bed size={14} />{property.rooms} Zimmer</span>
                            )}
                        </div>
                        <div className="flex items-center justify-between">
                            <p className="font-bold text-lg" style={{ fontFamily: 'var(--font-cabinet)' }}>
                                {formatPrice(property.price, property.currency, property.marketing_type)}
                                {property.marketing_type === 'rent' && !!property.price && <span className="text-sm font-normal">/Monat</span>}
                            </p>
                            <Badge variant="outline" className="rounded-sm">
                                {PROPERTY_TYPE_LABELS[property.property_type] || property.property_type}
                            </Badge>
                        </div>
                    </CardContent>
                </Card>
            </a>
        </div>
    );
}
